import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc,
  query, 
  where, 
  orderBy,
  onSnapshot,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { createIncidentLog } from '../utils/activityLogger';
import { 
  CRISIS_REPORT_STATUSES,
  CRISIS_REPORT_TYPES,
  CRISIS_REPORT_SEVERITIES
} from '../types/crisisReport';

/**
 * 🚨 Crisis Report Service for Triage Management
 * 
 * Provides read-only access to crisis reports submitted from mobile app
 * and status update operations for the web-based triage workflow.
 * 
 * SECURITY MODEL:
 * - All operations are protected by Firestore security rules
 * - Users can only access reports from their organization (orgId scoping)
 * - Only CMT members can update report status
 * - Direct creation is blocked (only Cloud Functions can create reports)
 * - Deletion is permanently disabled (audit trail preservation)
 * 
 * @author Crisis Compass Development Team
 * @version 1.0.0 - Crisis Reports Triage Implementation
 */

// Cache for performance optimization
const crisisReportCache = new Map();
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes for crisis reports (fresh data needed)

export const crisisReportService = {

  /**
   * 📋 Subscribe to crisis reports with real-time updates
   * @param {string} orgId - Organization ID (required for tenant isolation)
   * @param {Object} filters - Filter options { status?, type? }
   * @param {function} callback - Callback function for real-time updates
   * @returns {function} Unsubscribe function
   */
  subscribeToReports(orgId, filters = {}, callback) {
    try {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }

      if (typeof callback !== 'function') {
        throw new Error('Callback function is required');
      }

      console.log(`📡 Setting up crisis reports listener for org:${orgId}`, filters);

      const reportsRef = collection(db, 'orgs', orgId, 'reports');
      const queryConstraints = [
        orderBy('createdAt', 'desc')
      ];

      // Add status filter if specified
      if (filters.status) {
        queryConstraints.splice(0, 0, where('status', '==', filters.status));
      }

      // Add type filter if specified  
      if (filters.type) {
        queryConstraints.splice(filters.status ? 1 : 0, 0, where('type', '==', filters.type));
      }

      const q = query(reportsRef, ...queryConstraints);

      return onSnapshot(q, 
        (querySnapshot) => {
          console.log(`📡 Crisis reports update received: ${querySnapshot.size} documents`);
          
          const reports = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // Keep Firestore Timestamp objects as-is (don't convert to Date)
            createdAt: doc.data().createdAt,
            updatedAt: doc.data().updatedAt,
            acknowledgedAt: doc.data().acknowledgedAt,
            promotedAt: doc.data().promotedAt,
            declinedAt: doc.data().declinedAt,
            receivedAt: doc.data().receivedAt
          }));

          // Clear cache for this query
          const cacheKey = `reports:${orgId}:${filters.status || 'all'}:${filters.type || 'all'}`;
          this._clearCache(cacheKey);

          callback(reports);
        },
        (error) => {
          console.error('📡 Crisis reports listener error:', error);
          // Call callback with error to let UI handle it
          callback(null, error);
        }
      );

    } catch (error) {
      console.error('Error setting up crisis reports listener:', error);
      // Return no-op unsubscribe function
      return () => {};
    }
  },

  /**
   * 📊 Get real-time count of submitted reports
   * @param {string} orgId - Organization ID
   * @param {function} callback - Callback function for count updates
   * @returns {function} Unsubscribe function
   */
  getSubmittedCount(orgId, callback) {
    try {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }

      if (typeof callback !== 'function') {
        throw new Error('Callback function is required');
      }

      console.log(`📊 Setting up submitted count listener for org:${orgId}`);

      const reportsRef = collection(db, 'orgs', orgId, 'reports');
      const q = query(
        reportsRef,
        where('status', '==', CRISIS_REPORT_STATUSES.SUBMITTED)
      );

      return onSnapshot(q,
        (querySnapshot) => {
          const count = querySnapshot.size;
          console.log(`📊 Submitted reports count update: ${count}`);
          callback(count);
        },
        (error) => {
          console.error('📊 Submitted count listener error:', error);
          callback(0, error);
        }
      );

    } catch (error) {
      console.error('Error setting up submitted count listener:', error);
      return () => {};
    }
  },

  /**
   * 📄 Get specific crisis report by ID
   * @param {string} reportId - Crisis report ID
   * @param {string} orgId - Organization ID (required for subcollection path)
   * @returns {Promise<Object|null>} Crisis report object or null if not found
   */
  async getReportById(reportId, orgId) {
    try {
      if (!reportId) {
        throw new Error('Report ID is required');
      }

      if (!orgId) {
        throw new Error('Organization ID is required');
      }

      const docRef = doc(db, 'orgs', orgId, 'reports', reportId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
        // Keep Firestore Timestamp objects as-is
        createdAt: docSnap.data().createdAt,
        updatedAt: docSnap.data().updatedAt,
        acknowledgedAt: docSnap.data().acknowledgedAt,
        promotedAt: docSnap.data().promotedAt,
        declinedAt: docSnap.data().declinedAt,
        receivedAt: docSnap.data().receivedAt
      };

    } catch (error) {
      console.error('Error getting crisis report:', error);
      throw new Error(`Failed to get crisis report: ${error.message}`);
    }
  },

  /**
   * 🔄 Generic status update method
   * @param {string} reportId - Crisis report ID
   * @param {string} orgId - Organization ID (required for subcollection path)
   * @param {Object} patch - Status update patch
   * @returns {Promise<void>}
   */
  async updateReportStatus(reportId, orgId, patch) {
    try {
      if (!reportId) {
        throw new Error('Report ID is required');
      }

      if (!orgId) {
        throw new Error('Organization ID is required');
      }

      if (!patch || typeof patch !== 'object') {
        throw new Error('Status update patch is required');
      }

      const docRef = doc(db, 'orgs', orgId, 'reports', reportId);
      
      // Validate report exists
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error('Crisis report not found');
      }

      const updateData = {
        ...patch,
        updatedAt: serverTimestamp()
      };

      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      await updateDoc(docRef, updateData);

      console.log(`✅ Crisis report ${reportId} status updated:`, patch);

    } catch (error) {
      console.error('Error updating crisis report status:', error);
      throw new Error(`Failed to update crisis report status: ${error.message}`);
    }
  },

  /**
   * ✅ Acknowledge crisis report
   * @param {string} reportId - Crisis report ID
   * @param {string} orgId - Organization ID
   * @param {string} uid - UID of user acknowledging
   * @returns {Promise<void>}
   */
  async acknowledgeReport(reportId, orgId, uid) {
    try {
      if (!reportId || !orgId || !uid) {
        throw new Error('Report ID, Organization ID, and acknowledgedBy UID are required');
      }

      // Get the crisis report first for logging details
      const reportDoc = await getDoc(doc(db, 'orgs', orgId, 'reports', reportId));
      const reportData = reportDoc.exists() ? reportDoc.data() : null;

      await this.updateReportStatus(reportId, orgId, {
        status: CRISIS_REPORT_STATUSES.ACKNOWLEDGED,
        acknowledgedBy: uid,
        acknowledgedAt: serverTimestamp()
      });

      // Log the acknowledgment activity for dashboard tracking
      try {
        await createIncidentLog({
          incidentId: null, // Non-incident activity for dashboard tracking
          orgId: orgId,
          action: 'crisis_report_acknowledged',
          details: `Crisis report "${reportData?.shortRef || reportId}" acknowledged`,
          userId: uid,
          userEmail: null, // Will be resolved in activityLogger
          userFullName: null, // Will be resolved in activityLogger
          priority: 'normal',
          metadata: {
            reportId: reportId,
            reportShortRef: reportData?.shortRef,
            reportType: reportData?.type,
            reportSummary: reportData?.summary,
            context: 'crisis_report_triage'
          }
        });
        console.log(`📊 Crisis report acknowledgment logged for dashboard tracking`);
      } catch (logError) {
        console.warn('⚠️ Failed to log crisis report acknowledgment (non-critical):', logError);
      }

      console.log(`✅ Crisis report ${reportId} acknowledged by ${uid}`);

    } catch (error) {
      console.error('Error acknowledging crisis report:', error);
      throw new Error(`Failed to acknowledge crisis report: ${error.message}`);
    }
  },

  /**
   * ⬆️ Promote crisis report (escalate to incident)
   * @param {string} reportId - Crisis report ID
   * @param {string} orgId - Organization ID
   * @param {string} uid - UID of user promoting
   * @param {string} reviewNote - Review note explaining promotion
   * @returns {Promise<void>}
   */
  async promoteReport(reportId, orgId, uid, reviewNote) {
    try {
      if (!reportId || !orgId || !uid || !reviewNote) {
        throw new Error('Report ID, Organization ID, promotedBy UID, and review note are required');
      }

      // Get the crisis report first for logging details
      const reportDoc = await getDoc(doc(db, 'orgs', orgId, 'reports', reportId));
      const reportData = reportDoc.exists() ? reportDoc.data() : null;

      await this.updateReportStatus(reportId, orgId, {
        status: CRISIS_REPORT_STATUSES.PROMOTED,
        promotedByUid: uid,
        promotedAt: serverTimestamp(),
        reviewNote: reviewNote
      });

      // Log the promotion activity for dashboard tracking
      try {
        await createIncidentLog({
          incidentId: null, // Non-incident activity for dashboard tracking
          orgId: orgId,
          action: 'crisis_report_promoted',
          details: `Crisis report "${reportData?.shortRef || reportId}" promoted to incident`,
          userId: uid,
          userEmail: null, // Will be resolved in activityLogger
          userFullName: null, // Will be resolved in activityLogger
          priority: 'high',
          metadata: {
            reportId: reportId,
            reportShortRef: reportData?.shortRef,
            reportType: reportData?.type,
            reportSummary: reportData?.summary,
            reviewNote: reviewNote,
            context: 'crisis_report_triage'
          }
        });
        console.log(`📊 Crisis report promotion logged for dashboard tracking`);
      } catch (logError) {
        console.warn('⚠️ Failed to log crisis report promotion (non-critical):', logError);
      }

      console.log(`⬆️ Crisis report ${reportId} promoted by ${uid}`);

    } catch (error) {
      console.error('Error promoting crisis report:', error);
      throw new Error(`Failed to promote crisis report: ${error.message}`);
    }
  },

  /**
   * ⬇️ Decline crisis report (no action needed)
   * @param {string} reportId - Crisis report ID
   * @param {string} orgId - Organization ID
   * @param {string} uid - UID of user declining
   * @param {string} reviewNote - Review note explaining decline
   * @returns {Promise<void>}
   */
  async declineReport(reportId, orgId, uid, reviewNote) {
    try {
      if (!reportId || !orgId || !uid || !reviewNote) {
        throw new Error('Report ID, Organization ID, declinedBy UID, and review note are required');
      }

      // Get the crisis report first for logging details
      const reportDoc = await getDoc(doc(db, 'orgs', orgId, 'reports', reportId));
      const reportData = reportDoc.exists() ? reportDoc.data() : null;

      await this.updateReportStatus(reportId, orgId, {
        status: CRISIS_REPORT_STATUSES.DECLINED,
        declinedByUid: uid,
        declinedAt: serverTimestamp(),
        reviewNote: reviewNote
      });

      // Log the decline activity for dashboard tracking
      try {
        await createIncidentLog({
          incidentId: null, // Non-incident activity for dashboard tracking
          orgId: orgId,
          action: 'crisis_report_declined',
          details: `Crisis report "${reportData?.shortRef || reportId}" declined`,
          userId: uid,
          userEmail: null, // Will be resolved in activityLogger
          userFullName: null, // Will be resolved in activityLogger
          priority: 'normal',
          metadata: {
            reportId: reportId,
            reportShortRef: reportData?.shortRef,
            reportType: reportData?.type,
            reportSummary: reportData?.summary,
            reviewNote: reviewNote,
            context: 'crisis_report_triage'
          }
        });
        console.log(`📊 Crisis report decline logged for dashboard tracking`);
      } catch (logError) {
        console.warn('⚠️ Failed to log crisis report decline (non-critical):', logError);
      }

      console.log(`⬇️ Crisis report ${reportId} declined by ${uid}`);

    } catch (error) {
      console.error('Error declining crisis report:', error);
      throw new Error(`Failed to decline crisis report: ${error.message}`);
    }
  },

  /**
   * 📋 Get recent reports for dashboard (non-real-time)
   * @param {string} orgId - Organization ID
   * @param {number} limitCount - Number of reports to fetch (default 3)
   * @returns {Promise<Array>} Array of recent crisis reports
   */
  async getRecentReports(orgId, limitCount = 3) {
    try {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }

      const reportsRef = collection(db, 'orgs', orgId, 'reports');
      const q = query(
        reportsRef,
        where('status', '==', CRISIS_REPORT_STATUSES.SUBMITTED),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Keep Firestore Timestamp objects as-is
        createdAt: doc.data().createdAt,
        updatedAt: doc.data().updatedAt
      }));

    } catch (error) {
      console.error('Error getting recent crisis reports:', error);
      throw new Error(`Failed to get recent crisis reports: ${error.message}`);
    }
  },

  // ==========================================
  // 🔧 PRIVATE HELPER METHODS
  // ==========================================

  /**
   * 💾 Cache management
   * @private
   */
  _getFromCache(key) {
    const cached = crisisReportCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }
    return null;
  },

  _setCache(key, data) {
    crisisReportCache.set(key, {
      data,
      timestamp: Date.now()
    });
  },

  _clearCache(key) {
    crisisReportCache.delete(key);
  },

  _clearCacheForOrg(orgId) {
    const keysToDelete = [];
    for (const key of crisisReportCache.keys()) {
      if (key.includes(orgId)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => crisisReportCache.delete(key));
  }
};

export default crisisReportService;
